import {
  collection, doc, getDoc, getDocs, query, where, orderBy, limit, startAfter,
  setDoc, updateDoc, serverTimestamp, Timestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, COLLECTIONS } from './config';

// Search teachers with filters
export const searchTeachers = async (filters = {}) => {
  try {
    const teachersRef = collection(db, COLLECTIONS.USERS);
    // Single where to avoid composite index requirement, filter rest client-side
    const q = query(teachersRef, where('role', '==', 'teacher'));
    const snapshot = await getDocs(q);
    let teachers = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(t => t.profileApproved === true);

    // Filter by language client-side
    if (filters.language) {
      teachers = teachers.filter(t => t.languages?.includes(filters.language));
    }

    // Client-side filtering for price range
    if (filters.minPrice) {
      teachers = teachers.filter(t => (t.pricePerHour || 0) >= filters.minPrice);
    }
    if (filters.maxPrice) {
      teachers = teachers.filter(t => (t.pricePerHour || 0) <= filters.maxPrice);
    }

    // Filter by country
    if (filters.country) {
      teachers = teachers.filter(t => t.country === filters.country);
    }

    // Filter by specialties
    if (filters.specialty) {
      teachers = teachers.filter(t => t.specialties?.includes(filters.specialty));
    }

    // Filter by native speaker
    if (filters.nativeSpeaker) {
      teachers = teachers.filter(t => t.nativeLanguages?.includes(filters.nativeSpeaker));
    }

    // Sort
    switch (filters.sortBy) {
      case 'price_low':
        teachers.sort((a, b) => (a.pricePerHour || 0) - (b.pricePerHour || 0));
        break;
      case 'price_high':
        teachers.sort((a, b) => (b.pricePerHour || 0) - (a.pricePerHour || 0));
        break;
      case 'rating':
        teachers.sort((a, b) => (b.averageRating || 0) - (a.averageRating || 0));
        break;
      case 'lessons':
        teachers.sort((a, b) => (b.totalLessons || 0) - (a.totalLessons || 0));
        break;
      default:
        // Default: top picks (combination of rating and lessons)
        teachers.sort((a, b) => {
          const scoreA = (a.averageRating || 0) * 0.6 + Math.min((a.totalLessons || 0) / 100, 1) * 0.4;
          const scoreB = (b.averageRating || 0) * 0.6 + Math.min((b.totalLessons || 0) / 100, 1) * 0.4;
          return scoreB - scoreA;
        });
    }

    return teachers;
  } catch (error) {
    console.error('Error searching teachers:', error);
    throw error;
  }
};

// Get teacher public profile
export const getTeacherPublicProfile = async (teacherId) => {
  try {
    const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, teacherId));
    if (!userDoc.exists()) throw new Error('Teacher not found');

    const userData = userDoc.data();
    if (userData.role !== 'teacher') throw new Error('User is not a teacher');

    // Get reviews (graceful — don't fail if collection doesn't exist)
    let reviews = [];
    try {
      const reviewsQuery = query(
        collection(db, COLLECTIONS.REVIEWS),
        where('teacherId', '==', teacherId)
      );
      const reviewsSnap = await getDocs(reviewsQuery);
      reviews = reviewsSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(0);
          const dateB = b.createdAt?.toDate?.() || new Date(0);
          return dateB - dateA;
        })
        .slice(0, 20);
    } catch (e) {
      console.warn('Could not fetch reviews:', e.message);
    }

    // Get availability (graceful)
    let availability = [];
    try {
      const availQuery = query(
        collection(db, COLLECTIONS.AVAILABILITY),
        where('teacherId', '==', teacherId)
      );
      const availSnap = await getDocs(availQuery);
      availability = availSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (e) {
      console.warn('Could not fetch availability:', e.message);
    }

    return {
      id: teacherId,
      displayName: userData.displayName,
      firstName: userData.firstName,
      lastName: userData.lastName,
      photoURL: userData.photoURL,
      videoURL: userData.videoURL,
      bio: userData.bio,
      description: userData.description,
      languages: userData.languages || [],
      nativeLanguages: userData.nativeLanguages || [],
      specialties: userData.specialties || [],
      experience: userData.experience,
      education: userData.education,
      certifications: userData.certifications || [],
      country: userData.country,
      pricePerHour: userData.pricePerHour,
      currency: userData.currency || 'USD',
      phoneNumber: userData.phoneNumber || '',
      trialPrice: userData.trialPrice,
      lessonDuration: userData.lessonDuration || 50,
      averageRating: userData.averageRating || 0,
      totalReviews: userData.totalReviews || 0,
      totalLessons: userData.totalLessons || 0,
      totalStudents: userData.totalStudents || 0,
      isNew: (userData.totalLessons || 0) < 10,
      profileApproved: userData.profileApproved,
      createdAt: userData.createdAt,
      reviews,
      availability
    };
  } catch (error) {
    console.error('Error getting teacher profile:', error);
    throw error;
  }
};

// Update teacher profile
export const updateTeacherProfile = async (teacherId, profileData) => {
  try {
    const userRef = doc(db, COLLECTIONS.USERS, teacherId);
    // Remove undefined values — Firestore rejects them
    const cleanData = Object.fromEntries(
      Object.entries(profileData).filter(([_, v]) => v !== undefined)
    );
    await setDoc(userRef, {
      ...cleanData,
      updatedAt: serverTimestamp()
    }, { merge: true });
    return true;
  } catch (error) {
    console.error('Error updating teacher profile:', error);
    throw error;
  }
};

// Upload teacher photo
export const uploadTeacherPhoto = async (teacherId, file) => {
  try {
    const fileRef = ref(storage, `teachers/${teacherId}/profile.${file.name.split('.').pop()}`);
    await uploadBytes(fileRef, file);
    const url = await getDownloadURL(fileRef);
    await updateDoc(doc(db, COLLECTIONS.USERS, teacherId), { photoURL: url });
    return url;
  } catch (error) {
    console.error('Error uploading photo:', error);
    throw error;
  }
};

// Upload teacher intro video
export const uploadTeacherVideo = async (teacherId, file) => {
  try {
    const fileRef = ref(storage, `teachers/${teacherId}/intro.${file.name.split('.').pop()}`);
    await uploadBytes(fileRef, file);
    const url = await getDownloadURL(fileRef);
    await updateDoc(doc(db, COLLECTIONS.USERS, teacherId), { videoURL: url });
    return url;
  } catch (error) {
    console.error('Error uploading video:', error);
    throw error;
  }
};

// Set teacher availability
export const setTeacherAvailability = async (teacherId, slots) => {
  try {
    // Delete existing slots
    const existingQuery = query(
      collection(db, COLLECTIONS.AVAILABILITY),
      where('teacherId', '==', teacherId)
    );
    const existing = await getDocs(existingQuery);
    const deletePromises = existing.docs.map(d => {
      const ref = doc(db, COLLECTIONS.AVAILABILITY, d.id);
      return updateDoc(ref, { active: false });
    });
    await Promise.all(deletePromises);

    // Create new slots
    const createPromises = slots.map(slot => {
      const slotRef = doc(collection(db, COLLECTIONS.AVAILABILITY));
      return setDoc(slotRef, {
        teacherId,
        dayOfWeek: slot.dayOfWeek, // 0=Sunday, 6=Saturday
        startTime: slot.startTime, // "09:00"
        endTime: slot.endTime,     // "10:00"
        active: true,
        createdAt: serverTimestamp()
      });
    });
    await Promise.all(createPromises);
    return true;
  } catch (error) {
    console.error('Error setting availability:', error);
    throw error;
  }
};

// Get all available languages from teachers
export const getAvailableLanguages = async () => {
  try {
    const q = query(
      collection(db, COLLECTIONS.USERS),
      where('role', '==', 'teacher')
    );
    const snapshot = await getDocs(q);
    const languageSet = new Set();
    snapshot.docs.forEach(d => {
      const data = d.data();
      if (data.profileApproved) {
        (data.languages || []).forEach(lang => languageSet.add(lang));
      }
    });
    return Array.from(languageSet).sort();
  } catch (error) {
    console.error('Error getting languages:', error);
    return [];
  }
};
